/**
 * FP3-125 — Canonical session-participation classifier (frontend).
 *
 * Mirrors `backend/session_participation.py`. Every UI surface that
 * counts sessions (Student Workspace Tracker, Reports Attendance,
 * dashboards, Fidelity when enabled) should read from this helper
 * so definitions cannot drift between client and server.
 *
 * Product rules:
 *   Provider Unavailable  → session_outcome === "provider_unavailable"
 *                            (0 delivered minutes; NEVER classified as
 *                             Completed / Absent / Unavailable / Cancelled)
 *   Cancelled             → status in ("cancelled", "canceled", "missed")
 *   Held + Present        → status === "completed" AND per-student
 *                            attendance === "present"
 *   Held + Absent         → status === "completed" AND per-student
 *                            attendance === "absent"
 *   Held + Unavailable    → status === "completed" AND per-student
 *                            attendance === "unavailable"
 *   Scheduled             → status === "scheduled"
 *   Other                 → any other state
 */

export const PARTICIPATION = Object.freeze({
  COMPLETED: "completed",
  ABSENT: "absent",
  UNAVAILABLE: "unavailable",          // student unavailable
  PROVIDER_UNAVAILABLE: "provider_unavailable",
  CANCELLED: "cancelled",
  SCHEDULED: "scheduled",
  OTHER: "other",
});

/**
 * @param {object} session
 * @param {string|null} [studentId] — pass to pick per-student attendance;
 *   omit to use the session-level `attendance` field.
 * @returns {string} one of PARTICIPATION.*
 */
export function classifySessionParticipation(session, studentId) {
  if (!session || typeof session !== "object") return PARTICIPATION.OTHER;

  // Provider Unavailable takes precedence over `status` — a PU session
  // is documentation-completed but the STUDENT never received service.
  // Never count as attendance.
  const outcome = String(session.session_outcome || "").toLowerCase();
  if (outcome === "provider_unavailable") {
    return PARTICIPATION.PROVIDER_UNAVAILABLE;
  }

  const status = String(session.status || "").toLowerCase();
  if (status === "cancelled" || status === "canceled" || status === "missed") {
    return PARTICIPATION.CANCELLED;
  }
  if (status === "scheduled") {
    return PARTICIPATION.SCHEDULED;
  }
  if (status !== "completed") {
    return PARTICIPATION.OTHER;
  }

  // status === "completed" → per-student attendance
  let perStudent;
  if (studentId && session.student_attendance && typeof session.student_attendance === "object") {
    perStudent = session.student_attendance[studentId];
  }
  const att = String(perStudent || session.attendance || "present").toLowerCase();
  if (att === "absent") return PARTICIPATION.ABSENT;
  if (att === "unavailable") return PARTICIPATION.UNAVAILABLE;
  return PARTICIPATION.COMPLETED;
}
